// Foto de um CODIGO do fornecedor no Monitor de Custos — colar (Ctrl+V), arrastar ou escolher.
// Serve so pra IDENTIFICAR o item na hora de comprar (nao sai em catalogo/fly/NF), por isso nao tem
// recorte nem esteira de foto de produto: cola e salva. Existe porque o NOSSO produto nao diferencia
// embalagem — rolo 100m e bobina 500m sao o mesmo produto pra nos, e codigos distintos pro fornecedor.
import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, Check, Upload, Trash2, ImageUp, AlertTriangle, BadgeCheck } from 'lucide-react';
import { Modal } from '../../components/Modal';
import { api, type ApiError } from '../../lib/api';
import { useDialog } from '../../components/DialogProvider';
import { useToast } from '../../components/Toast';

const TIPOS_OK = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_BYTES = 8 * 1024 * 1024;

interface Props {
  ofertaId: number;
  codigo: string;
  descricao: string | null;
  fotoAtual: string | null;   // ja resolvida pela precedencia (colada > catalogo > produto raiz)
  fotoPropria: boolean;       // true = a foto atual foi colada NESTA oferta (da pra remover)
  // Nao ha prop de "tem original": quem responde isso e a avaliacao que o modal pede ao abrir
  // (estado 'sem_original'). Um flag da listagem duplicaria a mesma verdade em dois lugares.
  promovidaUrl: string | null; // ja virou foto do acervo? guarda pra onde foi
  onFechar: (mudou?: boolean) => void;
}

// Veredito da foto colada, medido pelo backend ao ABRIR o modal (roda a esteira de verdade).
// `estado` manda; os campos abaixo so explicam o 'nao_master'.
interface Avaliacao {
  estado: 'master' | 'nao_master' | 'sem_original' | 'ja_incluida' | 'indisponivel';
  alta_resolucao?: boolean;
  transparente?: boolean;
  largura?: number;
  altura?: number;
  foto_url?: string;
}

// Fotos do acervo visualmente iguais a que se quer incluir (indice de semelhanca, migration 0202).
interface FotoSemelhante {
  foto_url: string;
  nome: string | null;
  produtos: { id: number; codigo_interno: string | null; descricao: string }[];
}

export function ModalFotoOferta({ ofertaId, codigo, descricao, fotoAtual, fotoPropria, promovidaUrl, onFechar }: Props) {
  const toast = useToast();
  const dialog = useDialog();
  const fileRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null); // dataURL da imagem nova (ainda nao salva)
  const [mime, setMime] = useState<string>('image/png');
  const [salvando, setSalvando] = useState(false);
  const [removendo, setRemovendo] = useState(false);
  const [promovendo, setPromovendo] = useState(false);
  const [promovida, setPromovida] = useState<string | null>(promovidaUrl);
  const [avaliacao, setAvaliacao] = useState<Avaliacao | null>(null);
  const [semelhantes, setSemelhantes] = useState<FotoSemelhante[] | null>(null);

  const aceitar = useCallback((f: File | null | undefined) => {
    if (!f) return;
    if (!TIPOS_OK.includes(f.type)) { toast.erro('So JPG, PNG ou WebP'); return; }
    if (f.size > MAX_BYTES) { toast.erro('Imagem maior que 8MB'); return; }
    const reader = new FileReader();
    reader.onload = () => { setPreview(reader.result as string); setMime(f.type); };
    reader.readAsDataURL(f);
  }, [toast]);

  // Ctrl+V cola a imagem da area de transferencia (botao direito > "Copiar imagem" no site do fornecedor).
  useEffect(() => {
    function onPaste(e: ClipboardEvent) {
      for (const it of e.clipboardData?.items ?? []) {
        if (it.type.startsWith('image/')) {
          const f = it.getAsFile();
          if (f) { e.preventDefault(); aceitar(f); return; }
        }
      }
    }
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [aceitar]);

  async function salvar() {
    if (!preview || salvando) return;
    setSalvando(true);
    try {
      await api.post(`/api/monitor-precos/ofertas/${ofertaId}/foto`, { base64: preview, mime });
      toast.sucesso('Foto salva');
      onFechar(true);
    } catch (e) {
      toast.erro((e as Error).message);
    } finally {
      setSalvando(false);
    }
  }

  async function remover() {
    if (removendo) return;
    const ok = await dialog.confirmar('Remover a foto deste codigo?', { perigoso: true });
    if (!ok) return;
    setRemovendo(true);
    try {
      await api.delete(`/api/monitor-precos/ofertas/${ofertaId}/foto`);
      toast.sucesso('Foto removida');
      onFechar(true);
    } catch (e) {
      toast.erro((e as Error).message);
    } finally {
      setRemovendo(false);
    }
  }

  // Leva a foto pro acervo de fotos de produto. Entra SOLTA (sem produto) — vincular e escolha
  // humana la no acervo. O backend recusa o que nao alcanca o padrao Master e diz qual criterio
  // faltou; a recusa fica na tela (nao vira toast) porque ela e o que explica o que fazer.
  // Confere na ABERTURA se a foto alcanca o Master — o selo e o botao saem dessa medida, nao de
  // uma promessa. Nao roda enquanto ha `preview` (imagem nao salva ainda nao existe no servidor).
  useEffect(() => {
    if (!fotoPropria || preview) { setAvaliacao(null); return; }
    let vivo = true;
    setAvaliacao(null);
    api.get<Avaliacao>(`/api/monitor-precos/ofertas/${ofertaId}/foto-avaliacao`)
      .then((r) => { if (vivo) { setAvaliacao(r); if (r.estado === 'ja_incluida' && r.foto_url) setPromovida(r.foto_url); } })
      .catch(() => { if (vivo) setAvaliacao({ estado: 'indisponivel' }); });
    return () => { vivo = false; };
  }, [ofertaId, fotoPropria, preview, promovida]);

  async function promover(forcar = false) {
    if (promovendo) return;
    setPromovendo(true);
    if (forcar) setSemelhantes(null);
    try {
      const r = await api.post<{ ok: true; foto_url: string; nome: string }>(
        `/api/monitor-precos/ofertas/${ofertaId}/promover-foto`, forcar ? { forcar: true } : undefined);
      setPromovida(r.foto_url);
      toast.sucesso('Foto no acervo');
    } catch (e) {
      const corpo = (e as ApiError).corpo;
      // O 'nao_e_master' aqui virou rede de seguranca: o botao ja nasce apagado nesse caso, entao
      // so chega aqui se a foto mudou entre a avaliacao e o clique.
      if (corpo?.motivo === 'nao_e_master' && corpo.criterios) setAvaliacao({ estado: 'nao_master', ...corpo.criterios });
      else if (corpo?.motivo === 'ja_promovida') setPromovida(corpo.foto_url ?? null);
      else if (corpo?.motivo === 'ja_existe_parecida') setSemelhantes(corpo.semelhantes as FotoSemelhante[]);
      else toast.erro((e as Error).message);
    } finally {
      setPromovendo(false);
    }
  }

  const ocupado = salvando || removendo || promovendo;
  const mostrando = preview ?? fotoAtual;
  // O bloco de promocao so aparece na foto DESTA oferta: foto herdada do produto ja veio do acervo,
  // e imagem ainda nao salva (preview) nem existe no servidor pra ser promovida.
  const podePromover = fotoPropria && !preview;
  // O botao habilita SO com veredito de Master na mao. Sem avaliacao ainda (conferindo), com
  // veredito negativo, ja incluida, ou com o aviso de imagem repetida aberto, ele fica apagado —
  // em todos esses casos clicar nao levaria a lugar nenhum. Com o aviso na tela a decisao mora
  // nos botoes DELE ("Deixa pra la" / "Incluir mesmo assim").
  const podeClicar = avaliacao?.estado === 'master' && !promovida && !semelhantes && !ocupado;

  return (
    <Modal
      aberto
      onFechar={ocupado ? () => {} : () => onFechar(promovida !== promovidaUrl)}
      titulo={`Foto do codigo ${codigo}`}
      largura={460}
      rodape={
        <>
          <button type="button" className="btn-secundario" onClick={() => onFechar(promovida !== promovidaUrl)} disabled={ocupado}>Cancelar</button>
          {preview && (
            <button type="button" className="btn-primario flex items-center gap-1.5" onClick={salvar} disabled={salvando}>
              {salvando ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              {salvando ? 'Salvando...' : 'Salvar foto'}
            </button>
          )}
        </>
      }
    >
      <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
        onChange={(e) => { aceitar(e.target.files?.[0]); e.target.value = ''; }} />

      <div className="space-y-3">
        {descricao && <p className="text-[11px] opacity-60 leading-snug">{descricao}</p>}

        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); aceitar(e.dataTransfer?.files?.[0]); }}
          className="w-full border-2 border-dashed rounded-lg hover:bg-black/5 flex flex-col items-center justify-center gap-2 p-4"
          style={{ borderColor: 'var(--input-border, rgba(0,0,0,0.2))', minHeight: 210 }}
        >
          {mostrando ? (
            <img src={mostrando} alt="" style={{ maxHeight: 220, maxWidth: '100%', objectFit: 'contain', borderRadius: 6 }} />
          ) : (
            <>
              <Upload size={18} />
              <span className="text-sm font-medium">Cole a imagem com Ctrl+V</span>
              <span className="text-[11px] font-normal opacity-60">ou arraste pra ca · ou clique pra escolher um arquivo</span>
            </>
          )}
        </button>

        {mostrando && (
          <p className="text-[11px] opacity-60 text-center">
            {preview
              ? 'Imagem nova — clique em Salvar foto.'
              : fotoPropria
                ? 'Foto deste codigo. Cole (Ctrl+V) outra pra trocar.'
                : 'Foto vinda do produto do nosso cadastro (nao distingue a embalagem). Cole a foto do codigo pra trocar.'}
          </p>
        )}

        {podePromover && (
          // O SELO vem primeiro e o botao obedece a ele: a foto e medida ao abrir, e o que se ve
          // e' o veredito, nao uma promessa. Botao apagado sempre tem o selo do lado dizendo por que.
          <div>
            <div className="flex items-center gap-2 mb-2">
              {!avaliacao ? (
                <span className="text-[11.5px] flex items-center gap-1.5 opacity-70">
                  <Loader2 size={12} className="animate-spin" /> Conferindo a foto...
                </span>
              ) : avaliacao.estado === 'master' ? (
                <span className="text-[10px] font-extrabold tracking-wide px-2 py-1 rounded-full flex items-center gap-1"
                  style={{ background: 'var(--primary)', color: '#fff' }}>
                  <BadgeCheck size={11} /> MASTER
                </span>
              ) : avaliacao.estado === 'ja_incluida' || promovida ? (
                <span className="text-[10px] font-extrabold tracking-wide px-2 py-1 rounded-full flex items-center gap-1"
                  style={{ background: '#fde68a', color: '#000' }}>
                  <Check size={11} /> JA NO ACERVO
                </span>
              ) : (
                <span className="text-[10px] font-extrabold tracking-wide px-2 py-1 rounded-full flex items-center gap-1"
                  style={{ background: '#f97316', color: '#fff' }}>
                  <AlertTriangle size={11} /> {avaliacao.estado === 'sem_original' ? 'SO 600PX' : avaliacao.estado === 'indisponivel' ? 'NAO DEU PRA CONFERIR' : 'FORA DO PADRAO MASTER'}
                </span>
              )}
              {avaliacao?.estado === 'nao_master' && (
                <span className="text-[11px] opacity-70">
                  {!avaliacao.alta_resolucao && `${Math.max(avaliacao.largura ?? 0, avaliacao.altura ?? 0)}px (precisa 2000)`}
                  {!avaliacao.alta_resolucao && !avaliacao.transparente && ' · '}
                  {!avaliacao.transparente && 'fundo opaco'}
                </span>
              )}
            </div>

            <button type="button" onClick={() => promover()} disabled={!podeClicar}
              className="w-full py-2 text-[13.5px] font-semibold rounded-lg flex items-center justify-center gap-1.5 disabled:cursor-not-allowed"
              style={podeClicar
                ? { background: 'var(--primary)', color: '#fff' }
                : { background: 'var(--input-bg)', color: 'var(--text-muted, #8394b5)', border: '1px solid var(--input-border)' }}>
              {promovendo ? <Loader2 size={14} className="animate-spin" /> : promovida ? <Check size={14} /> : <ImageUp size={14} />}
              {promovendo ? 'Preparando a foto...' : 'Incluir no Acervo de Fotos'}
            </button>

            {semelhantes ? (
              // Aviso, nao bloqueio: o sistema so acha que sao a mesma imagem — quem sabe e' ele.
              // Mostra a foto e DE QUEM ela e', porque sem isso nao da pra julgar (ver o indice de
              // semelhanca, migration 0202; o limiar saiu de medicao no acervo real).
              <div className="rounded-lg p-2.5 mt-1.5" style={{ border: '1px solid #f97316', background: '#fff6ed' }}>
                <p className="text-[12.5px] font-bold mb-2 flex items-center gap-1.5" style={{ color: '#9a3412' }}>
                  <AlertTriangle size={13} /> Essa imagem ja parece estar no acervo
                </p>
                <div className="flex flex-wrap gap-2 mb-2">
                  {semelhantes.map((s) => (
                    <div key={s.foto_url} style={{ width: 96 }}>
                      <img src={s.foto_url} alt="" style={{ width: 96, height: 96, objectFit: 'contain', background: '#fff', border: '1px solid var(--input-border)', borderRadius: 4 }} />
                      <p className="text-[10px] leading-tight mt-1" style={{ color: '#3d4f75' }}>
                        {s.produtos[0]
                          ? <><b>{s.produtos[0].codigo_interno}</b> {s.produtos[0].descricao}</>
                          : (s.nome || 'sem produto')}
                      </p>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setSemelhantes(null)}
                    className="flex-1 py-1.5 text-[12px] rounded-md" style={{ background: 'var(--input-bg)', border: '1px solid var(--input-border)' }}>
                    Deixa pra la
                  </button>
                  <button type="button" onClick={() => promover(true)} disabled={promovendo}
                    className="flex-1 py-1.5 text-[12px] font-semibold rounded-md" style={{ background: '#f97316', color: '#fff' }}>
                    Incluir mesmo assim
                  </button>
                </div>
              </div>
            ) : (
              // Uma linha so. O que FALTA ja esta no selo acima (a caixa laranja que repetia os
              // criterios saiu: dizia com tres linhas o que o selo diz com duas palavras).
              <p className="text-[11.5px] leading-relaxed mt-1.5 opacity-70">
                {promovida
                  ? 'Ja incluida — esta no acervo sem produto. Vincule em Marketing > Gestao de Fotos > Acervo.'
                  : avaliacao?.estado === 'sem_original'
                    ? 'Foi colada antes de 07/08/2026, quando o sistema ainda descartava o arquivo original. Cole a foto de novo pra poder inclui-la.'
                    : avaliacao?.estado === 'nao_master'
                      ? 'O acervo so aceita foto no padrao Master. Trate a foto e cole a versao pronta — a daqui fica como esta.'
                      : avaliacao?.estado === 'indisponivel'
                        ? 'Nao deu pra medir a foto agora. Feche e abra o modal pra tentar de novo.'
                        : 'Entra no acervo SOLTA (sem produto), com a descricao do fornecedor como nome. Nao sobrescreve a foto de nenhum produto.'}
              </p>
            )}
          </div>
        )}

        {fotoPropria && !preview && (
          <button type="button" onClick={remover} disabled={removendo}
            className="w-full py-2 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50 flex items-center justify-center gap-1.5">
            {removendo ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
            {removendo ? 'Removendo...' : 'Remover foto'}
          </button>
        )}
      </div>
    </Modal>
  );
}
